/**
 * Reading of an uploaded code list, shared by every code-import feature (subscription card batches,
 * discount codes).
 *
 * For a CSV this mirrors the server parser: a plain split on the separator with no quoted-field
 * handling, blank lines skipped, and the header decided by line number. Keeping the two identical is
 * the point - what the dialog previews is what the import will read.
 *
 * Pure functions, no Angular, so they can be unit tested directly.
 */

/** Separators offered in the dialog. */
export const CODE_FILE_SEPARATORS = [',', ';', '|'];

/**
 * Extensions read as text.
 *
 * `.csv` only, and deliberately: both import endpoints decide with
 * `extension == ".csv" ? CreateCsvReader : CreateReader`, so anything else is opened as an Excel
 * workbook. A `.txt` would therefore fail to open and come back as a 400 on `file`, however
 * well-formed its contents.
 */
export const TEXT_EXTENSIONS = ['.csv'];

/** Extensions read as a workbook. Only offered where the server can parse one. */
export const SHEET_EXTENSIONS = ['.xlsx', '.xlsm', '.xls'];

/** Column labels that mean the first row names the columns rather than holding a code. */
const HEADER_LABELS = ['code', 'codes', 'card', 'card_id', 'cardid', 'card id', 'id', 'nummer'];

/** How many rows a header guess looks at before deciding the codes share one length. */
const HEADER_SAMPLE_ROWS = 20;

/**
 * A malformed line (a wrapped record, a pasted log line) can carry thousands of separators, and the
 * column count drives a select option and a table cell each. Past this many the file is not a code
 * list with columns to choose between.
 */
const MAX_COLUMNS = 50;

export interface ParsedCodeFile {
  /** The header row's cells, or null when the file has no header. */
  headerCells: string[] | null;
  dataRows: string[][];
  columnCount: number;
}

function extensionOf(fileName: string): string {
  const dot = (fileName ?? '').lastIndexOf('.');

  return dot >= 0 ? fileName.slice(dot).toLowerCase() : '';
}

export function isSpreadsheet(fileName: string): boolean {
  return SHEET_EXTENSIONS.includes(extensionOf(fileName));
}

/**
 * Whether this file is one the import can read at all.
 *
 * Anything that is not `.csv` is handed to the workbook reader, so a `.txt` (or a `.pdf`, or a photo)
 * fails to open and returns a 400 on `file`. `accept` on the input only steers the file picker and is
 * ignored by drag and drop, so the check has to happen after the file is in hand.
 */
export function hasSupportedExtension(fileName: string): boolean {
  const extension = extensionOf(fileName);

  return TEXT_EXTENSIONS.includes(extension) || SHEET_EXTENSIONS.includes(extension);
}

/** Catches a spreadsheet renamed `.csv`: text files do not carry NUL bytes, container formats do. */
export function looksBinary(text: string): boolean {
  for (let i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) === 0) return true;
  }

  return false;
}

export function splitLine(line: string, separator: string): string[] {
  return line.split(separator).map((cell) => cell.trim());
}

/** Physical lines, blanks included - the server decides the header by line number. */
export function toLines(text: string): string[] {
  return text.split(/\r\n|\r|\n/);
}

/** The separator that actually divides the file into more than one column, if any does. */
export function detectSeparator(lines: string[]): string {
  const sample = lines.filter((line) => line.trim().length > 0).slice(0, 10);
  if (!sample.length) return ',';

  const candidate = CODE_FILE_SEPARATORS.find((separator) => {
    const columns = sample[0].split(separator).length;
    return columns > 1 && sample.every((line) => line.split(separator).length === columns);
  });

  return candidate ?? ',';
}

/**
 * A header is either a recognised label, or a first row shaped unlike everything under it: every
 * code in a batch shares the configured length, so a differing length gives the header away.
 *
 * Judged on physical line 1, because that is the only line the server will skip. A file that opens
 * with a blank line therefore reports no header, which is correct - ticking the box there would
 * consume the blank line and upload the real header as a code.
 */
export function detectHasHeader(lines: string[], separator: string): boolean {
  const rows = lines.filter((line) => line.trim().length > 0);
  if (rows.length < 2) return false;

  const first = (lines[0]?.split(separator)[0] ?? '').trim();
  if (!first) return false;

  if (HEADER_LABELS.includes(first.toLowerCase())) return true;

  const lengths = new Set(
    rows
      .slice(1, HEADER_SAMPLE_ROWS)
      .map((line) => (line.split(separator)[0] ?? '').trim().length)
      .filter(Boolean)
  );

  return lengths.size === 1 && !lengths.has(first.length);
}

/** Same header decision for a spreadsheet, where the rows arrive already split into cells. */
export function detectSheetHasHeader(rows: string[][]): boolean {
  const lines = rows.map((row) => (row[0] ?? '').trim());

  return detectHasHeader(lines, ',');
}

export function parseCodeFile(lines: string[], separator: string, hasHeader: boolean): ParsedCodeFile {
  const [first, ...rest] = lines;

  const headerCells = hasHeader && first !== undefined ? splitLine(first, separator) : null;
  const dataRows = (hasHeader ? rest : lines)
    .filter((line) => line.trim().length > 0)
    .map((line) => splitLine(line, separator));

  return fromRows(headerCells, dataRows);
}

/** The spreadsheet path: cells are already split, so only the header and the counts are decided. */
export function parseSheetRows(rows: string[][], hasHeader: boolean): ParsedCodeFile {
  const [first, ...rest] = rows;

  const headerCells = hasHeader && first !== undefined ? first.map((cell) => (cell ?? '').trim()) : null;
  const dataRows = (hasHeader ? rest : rows)
    .map((row) => row.map((cell) => (cell ?? '').trim()))
    .filter((row) => row.some((cell) => cell.length > 0));

  return fromRows(headerCells, dataRows);
}

function fromRows(headerCells: string[] | null, dataRows: string[][]): ParsedCodeFile {
  // reduce, not Math.max(...rows): spreading a big file's rows as arguments overflows the call stack
  // somewhere past 100k, and the throw would escape the FileReader callback and hang the dialog.
  const widest = dataRows.reduce((max, row) => Math.max(max, row.length), headerCells?.length ?? 0);

  return {
    headerCells,
    dataRows,
    columnCount: Math.min(MAX_COLUMNS, Math.max(1, widest)),
  };
}
